import { lazy, type ComponentType } from "react";
import { isChunkReloadInFlight } from "./chunk-reload";

// `any` mirrors React.lazy's own bound.
type AnyComponent = ComponentType<any>;

/**
 * Loader for `React.lazy` that survives stale-chunk recovery.
 *
 * When a deploy invalidates a chunk, chunk-reload.ts preventDefault()s
 * Vite's `vite:preloadError` and reloads the page. Preventing it makes Vite's
 * import wrapper swallow the error and resolve `undefined`, so a bare
 * `.then((m) => m.X)` — or React reading `.default` — throws before the
 * reload lands (WEXARE-MVP-1J). While a reload is in flight we stay pending
 * so Suspense keeps its fallback up until the page is replaced.
 */
export function loadRoute<C extends AnyComponent>(
  load: () => Promise<{ default: C }>,
): Promise<{ default: C }>;
export function loadRoute<M, C extends AnyComponent>(
  load: () => Promise<M>,
  pick: (module: M) => C,
): Promise<{ default: C }>;
export function loadRoute<M, C extends AnyComponent>(
  load: () => Promise<M>,
  pick?: (module: M) => C,
): Promise<{ default: C }> {
  return load().then((module) => {
    if (module == null) {
      if (isChunkReloadInFlight()) return new Promise<never>(() => {});
      throw new Error("Lazy route import resolved to undefined");
    }
    return pick ? { default: pick(module) } : (module as unknown as { default: C });
  });
}

/** `React.lazy` over {@link loadRoute}. */
export function lazyRoute<C extends AnyComponent>(
  load: () => Promise<{ default: C }>,
): ReturnType<typeof lazy<C>>;
export function lazyRoute<M, C extends AnyComponent>(
  load: () => Promise<M>,
  pick: (module: M) => C,
): ReturnType<typeof lazy<C>>;
export function lazyRoute<M, C extends AnyComponent>(
  load: () => Promise<M>,
  pick?: (module: M) => C,
) {
  return lazy(() =>
    pick ? loadRoute(load, pick) : loadRoute(load as () => Promise<{ default: C }>),
  );
}
